import { type DefaultItem, Tooltip } from 'uikit';
import type {
    PropSize,
    Direction,
    PropGetItemKey,
    PropGetItemLabel,
    IconComponent,
    PropGetGroupKey,
    PropGetGroupLabel,
} from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { SearchableSelect } from '../SearchableSelect/SearchableSelect';

interface IControlledSearchableSelectField extends UseControllerProps<any> {
    size?: PropSize;
    items: DefaultItem[];
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    isLoading?: boolean;
    classNameTooltip?: string;
    direction?: Direction;
    isTooltipHidden?: boolean;
    getItemLabel?: PropGetItemLabel<any>;
    getItemKey?: PropGetItemKey<any>;
    getGroupLabel?: PropGetGroupLabel<any>;
    getGroupKey?: PropGetGroupKey<any>;
    searchDebounceTime?: number;
    onSearch?: (value: string) => void;
    itemsSearchPredicate?: (item: any, value: string) => void;
    label?: string;
    labelIcon?: IconComponent;
    className?: string;
    disabled?: boolean;
    labelPosition?: 'left' | 'top';
    isLazyLoadEnabled?: boolean;
    onNextPageRequest?: () => void;
    hasNextPage?: boolean;
}

const getItemLabelDefault = (item: any): string => item.label;
const getItemKeyDefault = (item: any): string | number => item.id;

export const ControlledSearchableSelectField: React.FC<IControlledSearchableSelectField> = (props) => {
    const {
        name,
        control,
        rules,
        size,
        items,
        onTooltipAttributeChange,
        isLoading,
        classNameTooltip,
        direction,
        isTooltipHidden,
        getItemLabel,
        getItemKey,
        searchDebounceTime,
        onSearch,
        itemsSearchPredicate,
        getGroupLabel,
        getGroupKey,
        label,
        labelIcon,
        className,
        disabled,
        labelPosition,
        isLazyLoadEnabled,
        onNextPageRequest,
        hasNextPage,
    } = props;

    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const onChange = useCallback(
        ({ value }) => {
            field.onChange(value);
        },
        [field],
    );

    const errorMessage = useMemo(
        () => (fieldState.invalid && fieldState.error?.message) || '',
        [fieldState.error?.message, fieldState.invalid],
    );

    const tooltipRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    return (
        <div>
            <SearchableSelect
                label={label}
                labelPosition={labelPosition}
                labelIcon={labelIcon}
                size={size}
                value={field.value}
                onChange={onChange}
                items={items}
                getItemLabel={getItemLabel || getItemLabelDefault}
                getItemKey={getItemKey || getItemKeyDefault}
                onSearch={onSearch}
                itemsSearchPredicate={itemsSearchPredicate}
                searchDebounceTime={searchDebounceTime}
                isLoading={isLoading}
                getGroupLabel={getGroupLabel as any}
                getGroupKey={getGroupKey as any}
                className={className}
                disabled={disabled}
                ref={anchorRef}
                status={errorMessage ? 'alert' : undefined}
                isLazyLoadEnabled={isLazyLoadEnabled}
                onNextPageRequest={onNextPageRequest}
                hasNextPage={hasNextPage}
                placeholder="Выберите"
            />
            {!isTooltipHidden && !!errorMessage && (
                <Tooltip
                    size="s"
                    anchorRef={anchorRef}
                    direction={direction}
                    possibleDirections={direction && [direction]}
                    status="alert"
                    className={`${classNameTooltip} tooltip-${name}`}
                    ref={tooltipRef}
                >
                    {errorMessage}
                </Tooltip>
            )}
        </div>
    );
};

ControlledSearchableSelectField.displayName = 'ControlledSearchableSelectField';
